<!-- Slide-over panel. Rendered only while a user is selected. -->
<div *ngIf="userId" class="ceo-drawer-root">
  <div class="ceo-drawer-backdrop" (click)="close()"></div>

  <aside class="ceo-drawer" role="dialog" aria-modal="true" aria-label="Customer detail">

    <!-- Header -->
    <header class="shrink-0 px-5 py-4 border-b border-slate-200 bg-white flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="flex items-center gap-2 min-w-0">
          <h2 class="text-base font-bold text-slate-900 truncate">{{ user?.name || 'Unnamed customer' }}</h2>
          <!-- ⭐ The plan, at a glance: the platform account plan for a platform user,
               the owned project plans for a legacy one (their plans live on projects). -->
          <span *ngFor="let badge of headerPlanBadges"
                class="shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded border"
                [ngClass]="badge.cls" [title]="badge.title">{{ badge.label }}</span>
        </div>
        <p class="text-xs text-slate-500 truncate">{{ user?.email }}</p>
      </div>
      <div class="shrink-0 flex items-center gap-1">
        <!-- ⭐ One-to-one email from a real person, starting on the last quick reply used. -->
        <button *ngIf="user?.email" type="button"
                class="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                (click)="contactOpen = true" title="Email this customer">
          <i class="fa-solid fa-paper-plane text-[11px]"></i><span>Contact</span>
        </button>
        <button type="button" class="w-8 h-8 rounded-lg grid place-items-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                (click)="close()" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
    </header>

    <!-- Loading / error -->
    <div *ngIf="loading" class="flex-1 grid place-items-center">
      <ion-spinner name="crescent"></ion-spinner>
    </div>
    <div *ngIf="!loading && errorMessage" class="flex-1 grid place-items-center p-6">
      <p class="text-sm text-red-600">{{ errorMessage }}</p>
    </div>

    <div *ngIf="!loading && user" class="flex-1 overflow-y-auto">

      <!-- Spend summary -->
      <section class="px-5 py-4 bg-slate-50 border-b border-slate-200">
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total spend</p>
            <p class="text-lg font-bold text-slate-900 tabular-nums">{{ formatEuros(user.totalSpend) }}</p>
          </div>
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Credits spend</p>
            <p class="text-lg font-bold text-slate-900 tabular-nums">{{ formatEuros(user.creditsSpend) }}</p>
          </div>
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Plans spend</p>
            <p class="text-lg font-bold text-slate-900 tabular-nums">{{ formatEuros(user.subscriptionsSpend) }}</p>
          </div>
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Balance</p>
            <!-- Purchased + plan allowance, matching the list column. The plan half is
                 broken out on its own row in the platform block below. -->
            <p class="text-lg font-bold text-slate-900 tabular-nums">{{ formatCredits(user.totalCredits) }}</p>
          </div>
        </div>
      </section>

      <!--
        Profile facts. Phone / onboarding / registered-from / VCaaS / origin were dropped:
        legacy fields nothing fills any more. In their place, the acquisition facts the
        CEO actually looks up — the table's Channel / Campaign / Detail plus the forensic
        ones (IP, where they landed). The full snapshot — raw user agent included — is the
        Acquisition tab.
      -->
      <section class="px-5 py-4 border-b border-slate-200">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
          <div><dt class="text-slate-500">Registered</dt><dd class="font-medium text-slate-800">{{ formatDate(user.createdAt) || '—' }}</dd></div>
          <div><dt class="text-slate-500">Last update</dt><dd class="font-medium text-slate-800">{{ formatDate(user.updatedAt) || '—' }}</dd></div>

          <div class="min-w-0">
            <dt class="text-slate-500">Channel</dt>
            <dd class="font-medium" [title]="acquisitionTitle">
              <span *ngIf="acquisitionChip as acq; else noChannel"
                    class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded border max-w-full"
                    [ngClass]="acq.kind === 'organic' ? '' : acquisitionChipClass(acq.kind)"
                    [ngStyle]="acq.kind === 'organic' ? hostColor(acq.channel) : null">
                <span class="truncate">{{ acq.channel }}</span>
              </span>
              <ng-template #noChannel><span class="text-slate-400">—</span></ng-template>
            </dd>
          </div>
          <div class="min-w-0">
            <dt class="text-slate-500">Campaign</dt>
            <dd class="font-medium truncate"
                [ngClass]="isSelfReportedAcquisition ? 'text-sky-700 italic' : 'text-slate-800'"
                [title]="acq?.acquisitionCampaign || ''">{{ acquisitionCampaignShort || '—' }}</dd>
          </div>

          <div class="min-w-0"><dt class="text-slate-500">Detail</dt><dd class="font-medium text-slate-800 truncate" [title]="acq?.acquisitionDetail || ''">{{ acq?.acquisitionDetail || '—' }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">Self-reported</dt><dd class="font-medium text-slate-800 truncate" [title]="selfReportedLabel">{{ selfReportedLabel || '—' }}</dd></div>

          <div class="min-w-0"><dt class="text-slate-500">Referrer</dt><dd class="font-medium text-slate-800 truncate" [title]="referrerLabel">{{ referrerLabel || '—' }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">Landing page</dt><dd class="font-medium text-slate-800 truncate" [title]="acq?.landingPath || ''">{{ acq?.landingPath || '—' }}</dd></div>

          <div class="min-w-0"><dt class="text-slate-500">IP</dt><dd class="font-mono text-[11px] break-all" [ngClass]="isLoopbackIp(acq?.ip) ? 'text-slate-400' : 'text-slate-700'"
                                                                        [title]="isLoopbackIp(acq?.ip) ? 'The server\'s own address — the visitor IP was not recorded for signups before 2026-09-10' : ''">{{ ipLabel(acq?.ip) || '—' }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">Location</dt><dd class="font-medium text-slate-800 truncate" [title]="locationLabel">{{ locationLabel || '—' }}</dd></div>

          <div class="min-w-0"><dt class="text-slate-500">Device</dt><dd class="font-medium text-slate-800 truncate">{{ deviceLabel || '—' }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">Browser · OS</dt><dd class="font-medium text-slate-800 truncate" [title]="browserLabel">{{ browserLabel || '—' }}</dd></div>

          <div class="min-w-0"><dt class="text-slate-500">Country (profile)</dt><dd class="font-medium text-slate-800 truncate">{{ countryName(user.country) || '—' }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">Language</dt><dd class="font-medium text-slate-800">{{ user.language || '—' }}</dd></div>

          <div><dt class="text-slate-500">Credits purchased</dt><dd class="font-medium text-slate-800">{{ formatEuros(user.totalCreditsPurchasedEuros) }}</dd></div>
          <div class="min-w-0"><dt class="text-slate-500">User id</dt><dd class="font-mono text-[11px] text-slate-700 break-all">{{ user._id }}</dd></div>

          <!-- The raw user agent lives only in the Acquisition tab (Browser fingerprint). -->
          <div *ngIf="acquisitionRows.length" class="col-span-2">
            <button type="button" class="text-[11px] text-slate-400 hover:text-slate-700 hover:underline underline-offset-2"
                    (click)="activeTab = 'acquisition'" title="Open the full acquisition snapshot">Full acquisition snapshot →</button>
          </div>
        </dl>
      </section>

      <!--
        ⭐ ENTER THIS ACCOUNT — platform accounts only (a legacy customer has no
        platform login; their PROJECTS are opened from the Projects list).

        ⚠️ ONE CLICK MINTS AND COPIES. The link is NOT rendered — it goes straight to
        the clipboard, because reading it is never useful and the control exists to be
        fast while a customer waits. The raw URL appears only when the clipboard write
        failed, so that a browser refusing to copy costs a manual copy, not the link.

        ⚠️ IT IS ONLY SAFE TO PUT ON A CLIPBOARD because what it carries is minted per
        click, single-use and dead in five minutes. Do not reuse this pattern for the
        project link, whose URL wraps a permanent credential.
      -->
      <section *ngIf="isPlatformUser" class="px-5 py-2 border-b border-slate-200 bg-slate-50/60">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="text-[11px] text-slate-500">Sign in as this customer — single use, expires in 5 minutes.</span>

          <button type="button"
                  class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg bg-slate-700 hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  [disabled]="loginLinkLoading"
                  title="Copies a link that signs you in as this customer. Single use, expires in 5 minutes."
                  (click)="createLoginLink()">
            <i class="fa-solid fa-right-to-bracket"></i>
            <span *ngIf="!loginLinkLoading">Enter account</span>
            <span *ngIf="loginLinkLoading">Working…</span>
          </button>
        </div>

        <!-- The password prompt, identical in behaviour to the project Open one. -->
        <div *ngIf="loginLinkNeedsPassword" class="mt-2 flex flex-wrap items-center gap-2">
          <input type="password" placeholder="Access password" autocomplete="off"
                 class="px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                 [(ngModel)]="loginLinkPassword"
                 (keyup.enter)="createLoginLink(loginLinkPassword)" />
          <button type="button"
                  class="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 hover:bg-white transition-colors"
                  [disabled]="!loginLinkPassword || loginLinkLoading"
                  (click)="createLoginLink(loginLinkPassword)">
            Unlock
          </button>
        </div>

        <!-- The normal outcome: copied, never shown. -->
        <p *ngIf="loginLinkCopied && !loginLinkRevealed"
           class="mt-2.5 text-xs font-medium text-emerald-700">
          <i class="fa-solid fa-check mr-1"></i>
          Login link copied. Paste it in another window — it stops working once used, or at
          {{ formatDate(loginLinkExpiresAt) }}.
        </p>

        <!-- Fallback: the clipboard refused, so the link has to be readable. -->
        <div *ngIf="loginLinkUrl && loginLinkRevealed" class="mt-2.5">
          <div class="flex items-center gap-2">
            <input type="text" readonly
                   class="flex-1 min-w-0 px-2.5 py-1.5 text-[11px] font-mono bg-white border border-slate-300 rounded-lg text-slate-700"
                   [value]="loginLinkUrl"
                   (focus)="$any($event.target).select()" />
            <button type="button"
                    class="shrink-0 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors"
                    [ngClass]="loginLinkCopied ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 hover:bg-white text-slate-700'"
                    (click)="copyLoginLink()">
              <i class="fa-solid mr-1" [ngClass]="loginLinkCopied ? 'fa-check' : 'fa-copy'"></i>
              {{ loginLinkCopied ? 'Copied' : 'Copy' }}
            </button>
          </div>
          <p class="mt-1.5 text-[11px] text-slate-500">
            Automatic copy was blocked by the browser. Copy it by hand — it stops working
            once used, or at {{ formatDate(loginLinkExpiresAt) }}.
          </p>
        </div>

        <p *ngIf="loginLinkError" class="mt-1.5 text-xs font-medium text-red-600">{{ loginLinkError }}</p>
      </section>

      <!--
        ═══ PLAN (NEW PLATFORM accounts only) ═══════════════════════════════════
        Shown ONLY when `platformAccount` is true. A legacy customer's plans belong
        to their projects (the Projects list below), never to their account, so this
        whole block is absent for them rather than showing them a Free badge they
        were never on.

        Deliberately a two-line strip: state on the first line, the control on the
        second. The explanation lives in the (i) tooltip and the history in a
        collapsed <details> — every feature is still here, just not spelled out.
      -->
      <section *ngIf="isPlatformUser" class="px-5 py-2 border-b border-slate-200 bg-indigo-50/40 text-[11px]">
        <!-- State -->
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <i class="fa-solid fa-layer-group mr-0.5 text-indigo-500"></i>Plan
          </span>
          <span class="px-1.5 py-px text-[10px] font-semibold rounded border"
                [ngClass]="planStyle(user.platformPlan)">{{ platformPlanLabel(user.platformPlan || 'free') }}</span>
          <span *ngIf="user.platformPlanIsManual"
                class="px-1 py-px text-[10px] font-semibold rounded border bg-amber-50 text-amber-700 border-amber-200 cursor-help"
                [title]="planGrantTitle">comp</span>
          <span class="text-slate-500">{{ user.platformPlanStatus || 'none' }}</span>
          <span class="text-slate-300">·</span>
          <span class="text-slate-500 tabular-nums">{{ formatCredits(user.platformRecurrentCredits) }} cr/mo</span>
          <span class="text-slate-300">·</span>
          <span class="text-slate-500">renews {{ formatDate(user.platformPlanRenewsAt) || '—' }}</span>
          <span *ngIf="planUsage" class="ml-auto text-[10px] tabular-nums"
                [ngClass]="planBudgetExhausted ? 'text-red-600 font-semibold' : 'text-slate-400'"
                title="Admin plan changes used today (resets 00:00 UTC)">{{ planUsage.used }}/{{ planUsage.max }} today</span>
        </div>

        <!--
          ⚠️ THE STRIPE REFUSAL IS SHOWN INSTEAD OF THE CONTROL, not next to it. A
          manual write on an account Stripe owns is reverted by the next invoice, so
          offering the form at all would be offering a change that silently undoes
          itself.
        -->
        <p *ngIf="planBlockedByStripe" class="mt-1 text-amber-700">
          <i class="fa-solid fa-triangle-exclamation mr-0.5"></i>
          Pays through Stripe — change it there; a manual change would be overwritten by the next invoice.
        </p>

        <!-- Control -->
        <div *ngIf="!planBlockedByStripe" class="mt-1 flex items-center gap-1.5">
          <select class="h-6 px-1 text-[11px] border border-slate-300 rounded bg-white focus:outline-none focus:border-blue-500"
                  [(ngModel)]="planChoice">
            <option *ngFor="let plan of grantablePlans" [value]="plan.id">
              {{ plan.name }} · {{ plan.creditsPerMonth }} cr/mo
            </option>
          </select>
          <input type="text" maxlength="300" placeholder="Reason (optional)"
                 class="h-6 flex-1 min-w-0 px-1.5 text-[11px] border border-slate-300 rounded bg-white focus:outline-none focus:border-blue-500"
                 [(ngModel)]="planReason" />
          <button type="button"
                  class="h-6 px-2 text-[11px] font-semibold text-white rounded bg-indigo-600 hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                  [disabled]="planUnchanged || planSubmitting || planBudgetExhausted"
                  (click)="submitPlan()">{{ planSubmitting ? 'Working…' : 'Change' }}</button>
          <i class="fa-solid fa-circle-info text-slate-400 cursor-help"
             title="No invoice, no charge. A paid plan grants its credits now and renews free every month until you change it back; dropping to Free disconnects GitHub and removes custom domains on every project."></i>
        </div>

        <p *ngIf="planBudgetExhausted" class="mt-1 text-red-600">Today's {{ planUsage?.max }} plan changes are used — resets at 00:00 UTC.</p>
        <p *ngIf="planFeedback" class="mt-1 font-medium text-emerald-700">{{ planFeedback }}</p>
        <p *ngIf="planError" class="mt-1 font-medium text-red-600">{{ planError }}</p>

        <!-- What has already been granted on this account. -->
        <details *ngIf="planChanges.length" class="mt-1">
          <summary class="cursor-pointer select-none text-[10px] text-slate-400 hover:text-slate-600">History ({{ planChanges.length }})</summary>
          <ul class="mt-1 space-y-0.5">
            <li *ngFor="let change of planChanges" class="flex items-baseline gap-2">
              <span class="text-slate-400 tabular-nums whitespace-nowrap">{{ formatDate(change.createdAt) }}</span>
              <span class="text-slate-700">
                {{ platformPlanLabel(change.fromPlan) }} → <strong>{{ platformPlanLabel(change.toPlan) }}</strong>
              </span>
              <span class="text-slate-400 truncate">{{ change.adminEmail }}<span *ngIf="change.reason"> · {{ change.reason }}</span></span>
            </li>
          </ul>
        </details>
      </section>

      <!--
        ═══ PROJECTS ═════════════════════════════════════════════════════════════
        Directly under the plan, not behind a tab: it is what the admin opens the
        panel for most often, and the plan above it is what the projects run on.
      -->
      <section class="px-5 py-3 border-b border-slate-200">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
          <i class="fa-solid fa-cubes mr-1 text-blue-500"></i> Projects
          <span class="ml-1 text-[10px] font-semibold text-slate-400">{{ projects.length }}</span>
        </h3>

        <div *ngIf="projects.length > 6" class="relative mb-3">
          <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400"></i>
          <input type="text" placeholder="Search projects by name or organization id"
                 class="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                 [(ngModel)]="projectSearch" />
        </div>

        <p *ngIf="!filteredProjects.length" class="text-xs text-slate-400 py-3 text-center">
          {{ projects.length ? 'No project matches that search' : 'This customer has no projects' }}
        </p>

        <div class="flex flex-col gap-2">
          <article *ngFor="let project of visibleProjects"
                   class="border border-slate-200 rounded-lg p-3 hover:border-slate-300 transition-colors">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-semibold text-slate-800 truncate">{{ project.description || project.projectDescription || 'Untitled project' }}</p>
                <p class="font-mono text-[10px] text-slate-400 truncate mt-0.5">{{ project.organizationId }}</p>
              </div>
              <button type="button"
                      class="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-60"
                      [disabled]="openingProjectId === project.organizationId"
                      (click)="openProject(project)">
                <span *ngIf="openingProjectId === project.organizationId">Opening…</span>
                <span *ngIf="openingProjectId !== project.organizationId">
                  Open <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px]"></i>
                </span>
              </button>
            </div>

            <div class="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px]">
              <span class="px-1.5 py-0.5 rounded border font-semibold"
                    [ngClass]="planStyle(projectTypeLabel(project))">{{ projectTypeLabel(project) || 'no plan' }}</span>
              <!-- What they set out to build — the classifier's read of the first prompt. -->
              <span class="px-1.5 py-0.5 rounded border font-semibold"
                    [ngClass]="projectCategoryStyle(project.projectCategory?.key)"
                    [title]="projectCategoryTitle(project.projectCategory)">
                <i class="fa-solid fa-shapes mr-0.5 text-[9px]"></i>{{ projectCategoryLabel(project.projectCategory?.key) }}
              </span>
              <span *ngIf="project.plan_anual === 'si'" class="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600">annual</span>
              <span *ngIf="project.billed && (project.price || 0) > 0" class="px-1.5 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 font-semibold">
                {{ formatEuros(project.price) }}
              </span>
              <span class="text-slate-400">·</span>
              <span class="text-slate-500">{{ formatCredits(project.totalCreditsSpent) }} credits used</span>
              <span class="text-slate-400">·</span>
              <span class="text-slate-500">{{ formatDate(project.createdAt) || 'no date' }}</span>
            </div>
            <!-- The one-line summary, and the first prompt itself on demand. -->
            <div *ngIf="project.projectCategory?.summary || project.firstPrompt?.excerpt" class="mt-2 text-[11px]">
              <p *ngIf="project.projectCategory?.summary" class="text-slate-600">{{ project.projectCategory?.summary }}</p>
              <details *ngIf="project.firstPrompt?.excerpt" class="mt-1">
                <summary class="cursor-pointer text-[10px] text-slate-400 hover:text-slate-600 select-none">
                  First prompt · {{ formatDate(project.firstPrompt?.at) }}
                </summary>
                <pre class="mt-1 p-2 rounded bg-slate-50 border border-slate-200 text-[10px] text-slate-600 whitespace-pre-wrap break-words max-h-48 overflow-y-auto">{{ project.firstPrompt?.excerpt }}{{ project.firstPrompt?.truncated ? ' …' : '' }}</pre>
              </details>
            </div>
          </article>
        </div>

        <button *ngIf="hasMoreProjects" type="button"
                class="mt-3 w-full py-2 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                (click)="showMoreProjects()">
          Show more ({{ filteredProjects.length - projectsShown }} left)
        </button>
      </section>

      <!--
        ═══ PROJECT LIMITS (NEW PLATFORM accounts only) ═════════════════════════
        How many projects this account may OWN, and how fast it may CREATE them.
        COLLAPSED BY DEFAULT — only the header row (usage + whether anything is
        pinned) shows until it is opened; the whole form is rarely needed.

        ⚠️ THE THREE FIELDS ARE OVERRIDES, NOT VALUES. Left empty they show the
        plan's default as a placeholder and write nothing; typed they pin that
        number for this one account until it is cleared again. That distinction is
        the whole design: an admin who raises one customer's ceiling must not
        accidentally freeze their creation rate at whatever the box happened to
        contain.

        ⚠️ `0` IS A REAL VALUE. `0` projects per minute freezes creation for this
        account without touching their plan or anything they have already built —
        which is what you want at 3am with an abusive signup. Empty ≠ zero.
      -->
      <section *ngIf="isPlatformUser" class="border-b border-slate-200 bg-slate-50/60">
        <button type="button"
                class="w-full px-5 py-2 flex items-center justify-between gap-2 text-left hover:bg-slate-100/70 transition-colors"
                [attr.aria-expanded]="limitsOpen"
                (click)="limitsOpen = !limitsOpen">
          <span class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <i class="fa-solid mr-1 text-[9px]" [ngClass]="limitsOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            <i class="fa-solid fa-sliders mr-0.5 text-slate-500"></i> Project limits
            <span *ngIf="hasAnyOverride" class="ml-1 px-1 py-px normal-case font-semibold rounded border bg-amber-50 text-amber-700 border-amber-200">custom</span>
          </span>
          <span *ngIf="projectLimits" class="text-[10px] font-semibold"
                [ngClass]="projectsAtCeiling ? 'text-red-600' : 'text-slate-400'">
            {{ projectLimits.used }} / {{ maxProjectsLabel }} in use
          </span>
        </button>

        <div *ngIf="limitsOpen" class="px-5 pb-4 pt-1">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <label class="block">
              <span class="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Max projects</span>
              <input type="number" min="0" step="1"
                     class="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                     [ngClass]="projectLimits?.overridden?.maxProjects ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-white'"
                     [placeholder]="planDefaultLabel('maxProjects')"
                     [(ngModel)]="limitsForm.maxProjects" />
            </label>

            <label class="block">
              <span class="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Projects / minute</span>
              <input type="number" min="0" step="0.1"
                     class="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                     [ngClass]="projectLimits?.overridden?.projectsPerMinute ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-white'"
                     [placeholder]="planDefaultLabel('projectsPerMinute')"
                     [(ngModel)]="limitsForm.projectsPerMinute" />
            </label>

            <label class="block">
              <span class="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Projects / hour</span>
              <input type="number" min="0" step="1"
                     class="w-full px-2.5 py-1.5 text-sm border rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                     [ngClass]="projectLimits?.overridden?.projectsPerHour ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-white'"
                     [placeholder]="planDefaultLabel('projectsPerHour')"
                     [(ngModel)]="limitsForm.projectsPerHour" />
            </label>
          </div>

          <div class="flex flex-wrap items-center gap-2 mt-2.5">
            <input type="text" maxlength="300" placeholder="Reason (optional)"
                   class="flex-1 min-w-[160px] px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                   [(ngModel)]="limitsReason" />

            <button type="button"
                    class="px-3.5 py-1.5 text-xs font-semibold text-white rounded-lg bg-slate-700 hover:bg-slate-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    [disabled]="limitsSubmitting || limitsUnchanged"
                    (click)="submitProjectLimits()">
              <span *ngIf="!limitsSubmitting">Save limits</span>
              <span *ngIf="limitsSubmitting">Working…</span>
            </button>

            <button type="button"
                    class="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    [disabled]="limitsSubmitting || !hasAnyOverride"
                    (click)="clearProjectLimits()">
              Use plan defaults
            </button>
          </div>

          <p class="mt-2 text-[11px] text-slate-500">
            Empty = whatever the plan says. <strong>{{ currentPlanLimitsSentence }}</strong>
            A rate below 1/minute is allowed: <code>0.2</code> means one project every five minutes.
          </p>

          <p *ngIf="projectLimits?.updatedByLabel" class="mt-1 text-[11px] text-slate-400">
            Last changed by {{ projectLimits?.updatedByLabel }}
          </p>

          <!-- What each tier allows, so an override can be judged against the ladder. -->
          <div *ngIf="limitsLadder.length" class="mt-3 pt-3 border-t border-slate-200">
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Plan defaults</p>
            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
              <li *ngFor="let tier of limitsLadder" class="flex items-baseline gap-2 text-[11px]">
                <span class="w-20 shrink-0 font-medium"
                      [ngClass]="tier.id === user.platformPlan ? 'text-indigo-700' : 'text-slate-600'">{{ tier.name }}</span>
                <span class="text-slate-500 tabular-nums">
                  {{ tier.maxProjects === 'unlimited' ? 'unlimited' : tier.maxProjects }} projects
                  · {{ tier.rateLabel }}
                  · {{ tier.projectsPerHour }}/hour
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Outside the collapsible: the result of a save must be visible even if it was closed. -->
        <p *ngIf="limitsFeedback" class="px-5 pb-2 text-xs font-medium text-emerald-700">{{ limitsFeedback }}</p>
        <p *ngIf="limitsError" class="px-5 pb-2 text-xs font-medium text-red-600">{{ limitsError }}</p>
      </section>

      <!-- Credits control: the only write in the whole CEO area -->
      <section class="px-5 py-4 border-b border-slate-200 bg-amber-50/40">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2.5">
          <i class="fa-solid fa-coins mr-1 text-amber-500"></i> Adjust credits
        </h3>

        <div class="flex flex-wrap items-center gap-2">
          <div class="flex bg-white border border-slate-200 rounded-lg p-0.5">
            <button type="button" class="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors"
                    [ngClass]="creditsMode === 'give' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'"
                    (click)="setCreditsMode('give')">Give</button>
            <button type="button" class="px-3 py-1.5 text-xs font-semibold rounded-md transition-colors"
                    [ngClass]="creditsMode === 'remove' ? 'bg-red-500 text-white' : 'text-slate-600 hover:bg-slate-50'"
                    (click)="setCreditsMode('remove')">Remove</button>
          </div>

          <input type="number" min="1" max="500000" step="1" placeholder="Amount"
                 class="w-28 px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                 [(ngModel)]="creditsAmount" />

          <button type="button"
                  class="px-3.5 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  [ngClass]="creditsMode === 'give' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'"
                  [disabled]="creditsAmountInvalid || creditsSubmitting"
                  (click)="submitCredits()">
            <span *ngIf="!creditsSubmitting">{{ creditsMode === 'give' ? 'Give credits' : 'Remove credits' }}</span>
            <span *ngIf="creditsSubmitting">Working…</span>
          </button>
        </div>

        <p class="mt-2 text-[11px] text-slate-500">Whole numbers, 1–500,000. Logged to the same audit trail as the support chat.</p>
        <p *ngIf="creditsFeedback" class="mt-1.5 text-xs font-medium text-emerald-700">{{ creditsFeedback }}</p>
        <p *ngIf="creditsError" class="mt-1.5 text-xs font-medium text-red-600">{{ creditsError }}</p>
      </section>

      <!-- A6.44: the account's v2 cost markups (CEO-only, second factor, audited) -->
      <section class="px-5 py-4 border-b border-slate-200 bg-violet-50/30">
        <app-ceo-v2-markups [accountUserId]="userId"></app-ceo-v2-markups>
      </section>

      <!-- Tabs -->
      <nav class="px-5 pt-4 flex gap-1.5 flex-wrap border-b border-slate-200">
        <button type="button" *ngFor="let tab of [
                  { id: 'purchases', label: 'Purchases', count: purchases.length },
                  { id: 'acquisition', label: 'Acquisition', count: acquisitionRows.length },
                  { id: 'credits', label: 'Credit history', count: detail?.creditPurchases?.length || 0 },
                  { id: 'payments', label: 'Payments', count: detail?.payments?.length || 0 }]"
                class="px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 -mb-px transition-colors"
                [ngClass]="activeTab === tab.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'"
                (click)="setTab($any(tab.id))">
          {{ tab.label }}
          <span class="ml-1 text-[10px] text-slate-400">{{ tab.count }}</span>
        </button>
      </nav>

      <!-- Purchases: the itemised real money behind totalSpend -->
      <section *ngIf="activeTab === 'purchases'" class="px-5 py-4">
        <div *ngIf="detail?.purchasesSummary" class="grid grid-cols-3 gap-2 mb-4">
          <div class="text-center p-2 bg-slate-50 rounded-lg">
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p class="text-sm font-bold text-slate-900">{{ formatEuros(detail?.purchasesSummary?.totalSpend) }}</p>
          </div>
          <div class="text-center p-2 bg-slate-50 rounded-lg">
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Credits</p>
            <p class="text-sm font-bold text-slate-900">{{ formatEuros(detail?.purchasesSummary?.creditsSpend) }}</p>
          </div>
          <div class="text-center p-2 bg-slate-50 rounded-lg">
            <p class="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Plans</p>
            <p class="text-sm font-bold text-slate-900">{{ formatEuros(detail?.purchasesSummary?.subscriptionsSpend) }}</p>
          </div>
        </div>

        <p *ngIf="!purchases.length" class="text-xs text-slate-400 py-8 text-center">
          This customer hasn't spent any money yet
        </p>

        <div class="flex flex-col gap-2">
          <div *ngFor="let purchase of purchases"
               class="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-2.5">
            <div class="flex items-center gap-2.5 min-w-0">
              <span class="shrink-0 w-8 h-8 rounded-lg grid place-items-center"
                    [ngClass]="purchase.type === 'credits' ? 'bg-amber-50 text-amber-600' : 'bg-violet-50 text-violet-600'">
                <i class="fa-solid" [ngClass]="purchase.type === 'credits' ? 'fa-coins' : 'fa-cube'"></i>
              </span>
              <div class="min-w-0">
                <p class="text-xs font-semibold text-slate-800 truncate">{{ purchase.description }}</p>
                <p class="text-[11px] text-slate-500">
                  {{ formatDate(purchase.date) || 'no date' }}
                  <span *ngIf="purchase.organizationId" class="font-mono text-slate-400"> · {{ purchase.organizationId }}</span>
                </p>
              </div>
            </div>
            <span class="shrink-0 text-sm font-bold text-emerald-700 tabular-nums">{{ formatEuros(purchase.amount) }}</span>
          </div>
        </div>

        <p class="mt-3 text-[10px] text-slate-400">
          Real money only. Free credit grants (support chat, referral bonuses) are under Credit history.
        </p>
      </section>

      <!-- Acquisition -->
      <section *ngIf="activeTab === 'acquisition'" class="px-5 py-4">
        <p *ngIf="!acquisitionGroups.length" class="text-xs text-slate-400 py-6 text-center">
          No acquisition data was captured for this customer
        </p>
        <div *ngFor="let group of acquisitionGroups; let first = first"
             class="text-xs" [ngClass]="first ? '' : 'mt-4 pt-3 border-t border-slate-100'">
          <h4 class="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-2">{{ group.title }}</h4>
          <p *ngIf="group.note" class="mb-2 text-[11px] text-slate-400">{{ group.note }}</p>
          <dl *ngIf="group.rows.length" class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div *ngFor="let row of group.rows" class="min-w-0" [class.sm:col-span-2]="row.wide">
              <dt class="text-slate-500">{{ row.label }}</dt>
              <dd class="break-all"
                  [ngClass]="[
                    row.mono ? 'font-mono text-[11px]' : 'font-medium',
                    row.tone === 'danger' ? 'text-red-600 font-bold'
                      : row.tone === 'warn' ? 'text-amber-700'
                      : row.tone === 'muted' ? 'text-slate-400'
                      : 'text-slate-800'
                  ]">{{ row.value }}</dd>
            </div>
          </dl>
        </div>
      </section>

      <!-- Credit history -->
      <section *ngIf="activeTab === 'credits'" class="px-5 py-4">
        <p *ngIf="!detail?.creditPurchases?.length" class="text-xs text-slate-400 py-6 text-center">No credit movements</p>
        <div class="flex flex-col gap-2">
          <div *ngFor="let purchase of detail?.creditPurchases" class="flex items-start justify-between gap-3 border-b border-slate-100 pb-2">
            <div class="min-w-0">
              <p class="text-xs font-medium text-slate-800">{{ purchase.origin }}</p>
              <p class="text-[11px] text-slate-500 break-words">{{ purchase.description || '—' }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">{{ formatDate(purchase.createdAt) }}</p>
            </div>
            <span class="shrink-0 text-xs font-bold tabular-nums"
                  [ngClass]="purchase.creditsQuantity >= 0 ? 'text-emerald-600' : 'text-red-600'">
              {{ purchase.creditsQuantity >= 0 ? '+' : '' }}{{ formatCredits(purchase.creditsQuantity) }}
            </span>
          </div>
        </div>
      </section>

      <!-- Payments -->
      <section *ngIf="activeTab === 'payments'" class="px-5 py-4">
        <p *ngIf="!detail?.payments?.length" class="text-xs text-slate-400 py-6 text-center">No payments</p>
        <div class="flex flex-col gap-2">
          <div *ngFor="let payment of detail?.payments" class="flex items-start justify-between gap-3 border-b border-slate-100 pb-2">
            <div class="min-w-0">
              <p class="text-xs font-medium text-slate-800">{{ payment.paymentType || 'unknown' }}<span *ngIf="payment.usageType" class="text-slate-400"> · {{ payment.usageType }}</span></p>
              <p class="font-mono text-[10px] text-slate-400 truncate">{{ payment.organizationId || '—' }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">{{ formatDate(payment.paymentCreated) }}</p>
            </div>
            <span class="shrink-0 text-xs font-bold text-slate-800 tabular-nums">{{ formatEuros(payment.totalImport) }}</span>
          </div>
        </div>
      </section>

      <!-- Deliberately faint: deletes the account AND all its projects (confirmation modal below). -->
      <div *ngIf="detail" class="px-5 pt-6 pb-4 text-right">
        <!-- Free accounts only: deletes every project, keeps the account and its credits (modal below). -->
        <button *ngIf="canDeleteAllProjects" type="button" class="mr-4 text-[10px] text-slate-300 hover:text-red-500 hover:underline"
                (click)="openDeleteProjects()">Delete all projects</button>
        <button type="button" class="text-[10px] text-slate-300 hover:text-red-500 hover:underline"
                (click)="openDeleteUser()">Delete account</button>
      </div>

    </div>
  </aside>
</div>

<!-- ═══ ⚠️ DELETE ALL PROJECTS OF A FREE ACCOUNT (the account and its credits stay) ═══════════
     Every call is re-checked on the server: v2 → totalum-accounts (free plan, write password, typed e-mail);
     legacy → account-backend's CEO project delete, one by one (access password, paid plans refused).
-->
<div *ngIf="deleteProjectsOpen && userId" class="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/50 p-4"
     (click)="closeDeleteProjects()">
  <div class="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" (click)="$event.stopPropagation()">
    <h3 class="text-sm font-semibold text-slate-900">Delete all {{ deleteProjectsCounts.v2 + deleteProjectsCounts.legacy }} projects?</h3>
    <p class="mt-0.5 text-xs text-slate-500 break-words">
      <span class="font-medium text-slate-700">{{ user?.name || 'Unnamed' }}</span> · {{ user?.email }}
    </p>
    <p class="mt-4 text-xs text-red-800/80">
      Deletes every project of this free account ({{ deleteProjectsCounts.v2 }} new-engine, {{ deleteProjectsCounts.legacy }} legacy),
      with their published sites and domains. The account, its API keys and its credits stay.
    </p>
    <p class="mt-2 text-[11px] text-slate-400">This cannot be undone from the panel.</p>

    <ng-container *ngIf="!deleteProjectsResults?.length || deletingProjects">
      <label class="mt-4 block text-xs font-medium text-slate-700">
        Type <span class="font-mono select-all text-slate-900">{{ user?.email }}</span> to confirm
      </label>
      <input type="text" autocomplete="off" spellcheck="false"
             class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
             [(ngModel)]="deleteProjectsConfirm" [disabled]="deletingProjects" />
      <ng-container *ngIf="legacyProjects.length">
        <label class="mt-3 block text-xs font-medium text-slate-700">Access password</label>
        <input type="password" autocomplete="off" placeholder="Access password"
               class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
               [(ngModel)]="deleteProjectsPassword" [disabled]="deletingProjects" />
      </ng-container>
      <ng-container *ngIf="v2Projects.length">
        <label class="mt-3 block text-xs font-medium text-slate-700">v2 write password</label>
        <input type="password" autocomplete="off" placeholder="v2 write password"
               class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
               [(ngModel)]="deleteProjectsV2Password" [disabled]="deletingProjects" />
      </ng-container>
    </ng-container>

    <p *ngIf="deleteProjectsProgress" class="mt-3 text-xs text-slate-600">{{ deleteProjectsProgress }}</p>
    <div *ngIf="deleteProjectsResults?.length && !deletingProjects" class="mt-3">
      <p class="text-xs font-semibold text-slate-800">{{ deleteProjectsSummary }}</p>
      <ul class="mt-1 max-h-40 overflow-y-auto text-[11px]">
        <li *ngFor="let r of deleteProjectsResults" class="break-words" [class.text-red-600]="!r.deleted" [class.text-slate-500]="r.deleted">
          <span class="font-mono">{{ r.projectId }}</span> — {{ r.deleted ? 'deleted' : r.error }}
        </li>
      </ul>
    </div>
    <p *ngIf="deleteProjectsError" class="mt-2 text-xs font-medium text-red-600">{{ deleteProjectsError }}</p>

    <div class="mt-4 flex justify-end gap-2">
      <button type="button" class="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
              [disabled]="deletingProjects" (click)="closeDeleteProjects()">{{ deleteProjectsResults?.length ? 'Close' : 'Cancel' }}</button>
      <button *ngIf="!deleteProjectsResults?.length || deletingProjects" type="button"
              class="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              [disabled]="!deleteProjectsReady" (click)="confirmDeleteProjects()">
        {{ deletingProjects ? 'Deleting…' : 'Delete all projects' }}
      </button>
    </div>
  </div>
</div>

<!-- ═══ ⚠️ DELETE USER ACCOUNT + ALL ITS PROJECTS ═══════════════════════════════
     CEO-only: the server re-checks the CEO session, the access password and the typed email.
-->
<div *ngIf="deleteUserOpen && userId" class="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-900/50 p-4"
     (click)="closeDeleteUser()">
  <div class="w-full max-w-md rounded-xl bg-white p-5 shadow-xl" (click)="$event.stopPropagation()">
    <div class="flex items-start gap-3">
      <span class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-red-100 text-red-600">
        <i class="fa-solid fa-triangle-exclamation text-sm"></i>
      </span>
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-slate-900">Delete this user account?</h3>
        <p class="mt-0.5 text-xs text-slate-500 break-words">
          <span class="font-medium text-slate-700">{{ user?.name || 'Unnamed' }}</span> · {{ user?.email }}
        </p>
      </div>
    </div>

    <p class="mt-4 text-xs text-red-800/80">
      Deletes the user record, their API keys and <b>all {{ projects.length }} of their projects</b>
      (published sites, preview snapshots, custom domains, sandboxes). Billing history is kept.
      Refused if they have a live subscription or a paid legacy project.
    </p>
    <p class="mt-2 text-[11px] text-slate-400">This cannot be undone from the panel.</p>

    <label class="mt-4 block text-xs font-medium text-slate-700">
      Type <span class="font-mono select-all text-slate-900">{{ user?.email }}</span> to confirm
    </label>
    <input type="text"
           class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
           autocomplete="off" spellcheck="false"
           [(ngModel)]="deleteUserConfirm"
           [disabled]="deletingUser"
           (keydown.enter)="confirmDeleteUser()" />

    <label class="mt-3 block text-xs font-medium text-slate-700">Access password</label>
    <input type="password"
           class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
           placeholder="Access password"
           autocomplete="off"
           [(ngModel)]="deleteUserPassword"
           [disabled]="deletingUser"
           (keydown.enter)="confirmDeleteUser()" />

    <p *ngIf="deleteUserError" class="mt-2 text-xs font-medium text-red-600">{{ deleteUserError }}</p>

    <div class="mt-4 flex justify-end gap-2">
      <button type="button" class="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-50"
              [disabled]="deletingUser"
              (click)="closeDeleteUser()">Cancel</button>
      <button type="button"
              class="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              [disabled]="!deleteUserConfirmed || !deleteUserPassword.trim() || deletingUser"
              (click)="confirmDeleteUser()">
        {{ deletingUser ? 'Deleting…' : 'Delete user and projects' }}
      </button>
    </div>
  </div>
</div>

<!-- ═══ ⭐⭐ THE ACCESS PASSWORD (same flow as the projects page) ═══════════════
     ⚠️ NOT THE GATE — the server re-checks on every call, in constant time, with a
     lockout. See `ceo-open-project.service.ts`. Asked once per browser.
-->
<div *ngIf="passwordFor" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
     (click)="closePasswordPanel()">
  <div class="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl" (click)="$event.stopPropagation()">
    <div class="flex items-start gap-3">
      <span class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700">
        <i class="fa-solid fa-lock text-sm"></i>
      </span>
      <div class="min-w-0">
        <h3 class="text-sm font-semibold text-slate-900">Access password required</h3>
        <p class="mt-0.5 text-xs text-slate-500">
          Opening <span class="font-mono text-slate-700">{{ passwordFor.organizationId }}</span> signs you in
          as that customer. Enter the access password once — it is remembered in this browser.
        </p>
      </div>
    </div>

    <input type="password"
           class="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
           placeholder="Access password"
           autocomplete="off"
           [(ngModel)]="passwordInput"
           (keydown.enter)="submitPassword()"
           autofocus />

    <p *ngIf="passwordError" class="mt-2 text-xs font-medium text-red-600">{{ passwordError }}</p>

    <div class="mt-4 flex justify-end gap-2">
      <button type="button" class="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
              (click)="closePasswordPanel()">Cancel</button>
      <button type="button"
              class="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-60"
              [disabled]="!passwordInput.trim() || !!openingProjectId"
              (click)="submitPassword()">
        {{ openingProjectId ? 'Checking…' : 'Unlock and open' }}
      </button>
    </div>
  </div>
</div>

<div *ngIf="openError" class="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-red-200 bg-red-50 p-3 shadow-lg">
  <div class="flex items-start gap-2">
    <i class="fa-solid fa-circle-exclamation mt-0.5 text-red-500"></i>
    <p class="flex-1 text-xs text-red-800">{{ openError }}</p>
    <button type="button" class="text-red-400 hover:text-red-700" (click)="openError = null" aria-label="Dismiss">
      <i class="fa-solid fa-times text-xs"></i>
    </button>
  </div>
</div>

<!-- The "Contact" modal. Stacks above the drawer (z-1100); `contactOpen` is reset in
     `ngOnChanges` whenever another customer is opened or the panel closes. -->
<app-ceo-contact-modal [open]="contactOpen && !!userId"
                       [userId]="userId"
                       [userName]="user?.name || ''"
                       [userEmail]="user?.email || ''"
                       [unsubscribed]="user?.unsubscribedEmailMarketing === true"
                       (closed)="contactOpen = false"></app-ceo-contact-modal>
